import {
  addDays,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  subDays,
} from "date-fns";

export const isDateInSaleStepRange = (date: Date, minDate?: Date, maxDate?: Date): boolean => {
  const day = startOfDay(date);
  if (minDate && isBefore(day, startOfDay(minDate))) return false;
  if (maxDate && isAfter(day, startOfDay(maxDate))) return false;
  return true;
};

export const clampToSaleStepRange = (date: Date, minDate?: Date, maxDate?: Date): Date => {
  const day = startOfDay(date);
  if (minDate && isBefore(day, startOfDay(minDate))) return startOfDay(minDate);
  if (maxDate && isAfter(day, startOfDay(maxDate))) return startOfDay(maxDate);
  return day;
};

export const getInitialAnchorDate = (minDate?: Date, maxDate?: Date): Date => {
  const today = startOfDay(new Date());
  const base = minDate ?? today;
  return clampToSaleStepRange(base, minDate, maxDate);
};

export const getScheduleDateRange = (
  anchorDate: Date,
  isWeekly: boolean,
  isMobile: boolean,
): { dateFrom: Date; dateTo: Date } => {
  if (!isWeekly) {
    return { dateFrom: anchorDate, dateTo: anchorDate };
  }

  if (isMobile) {
    return { dateFrom: anchorDate, dateTo: anchorDate };
  }

  return { dateFrom: anchorDate, dateTo: addDays(anchorDate, 6) };
};

export const getDayColumns = (anchorDate: Date, isWeekly: boolean, isMobile: boolean): string[] => {
  if (!isWeekly) return [];

  const daysCount = isMobile ? 1 : 7;
  return Array.from({ length: daysCount }, (_, i) => format(addDays(anchorDate, i), "yyyy-MM-dd"));
};

export const getLessonDateKey = (lesson: { startTime: number }): string =>
  format(new Date(lesson.startTime), "yyyy-MM-dd");

export const canNavigatePrevious = (
  anchorDate: Date,
  effectiveMin: Date,
  isWeekly: boolean,
  isMobile: boolean,
): boolean => {
  if (isWeekly) {
    const step = isMobile ? 1 : 7;
    const prev = subDays(anchorDate, step);
    return startOfDay(prev) >= startOfDay(effectiveMin);
  }
  return startOfDay(anchorDate) > startOfDay(effectiveMin);
};

export const canNavigateNext = (
  anchorDate: Date,
  maxDate: Date | undefined,
  isWeekly: boolean,
  isMobile: boolean,
): boolean => {
  if (!maxDate) return true;

  if (isWeekly) {
    const step = isMobile ? 0 : 6;
    const endOfPeriod = addDays(anchorDate, step);
    return startOfDay(endOfPeriod) < startOfDay(maxDate);
  }

  return startOfDay(anchorDate) < startOfDay(maxDate);
};

export const isBeforeNow = (hour: string, date: Date): boolean => {
  const now = new Date();
  if (!isSameDay(date, now)) return isBefore(startOfDay(date), startOfDay(now));
  const [h] = hour.split(":").map(Number);
  return h < now.getHours();
};
